/**
 * @fileoverview A function for fetching an account's Listings Settings
 *
 * @author Heesun Jun (hjun@yext.com)
 */

import Uri from 'goog:goog.Uri';

import {Routes} from '/src/com/yext/listingslaunchstorm/js/routes';

import {doRequest} from '/src/com/yext/listingslaunchstorm/js/common/util/dorequest';
import {mockRequest} from '/src/com/yext/listingslaunchstorm/js/common/util/mockrequest';
import {AltBrandNamesConfig} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/altbrandnamesconfig';
import {DeliveryConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/deliveryconfiguration';
import {ListingConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/listingsconfiguration';
import {LoadListingsConfigurationResponse} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/data/loadlistingsconfigurationresponse';

/**
 * @param {AbortSignal} abortSignal
 * @return {Promise<LoadListingsConfigurationResponse>}
 */
export function getListingConfiguration(abortSignal) {
  const route = new Uri(Routes.getListingConfig());
  return doRequest('GET_LISTING_CONFIG', abortSignal, route, 'GET')
    .then(json => LoadListingsConfigurationResponse.fromJSON(json));
}

export const DEFAULT_DELIVERY_CONFIGS = new Map([
  [715, new DeliveryConfiguration(
    {dontSyncListings: false, dontCreateNewListings: false, automaticallyOptIn: false})],
  [559, new DeliveryConfiguration(
    {dontSyncListings: false, dontCreateNewListings: false, automaticallyOptIn: false})],
  [0, new DeliveryConfiguration(
    {dontSyncListings: false, dontCreateNewListings: false, automaticallyOptIn: false})],
]);

const defaultConfig =
  new LoadListingsConfigurationResponse({
    alternateBrandNames: [
      new AltBrandNamesConfig({'id': 'testId', 'name': 'testName', 'description': 'testDescription'}),
      new AltBrandNamesConfig({'id': 'location.rank_tracking_alternate_names',
        'name': 'Rank Tracking Alternate Names',
        'description': 'Alternate names we can use to affect ranking of matches.'}),
    ],
    existingConfig: new ListingConfiguration({
      profileFieldId: '',
      deliveryConfigurationMap: DEFAULT_DELIVERY_CONFIGS,
      applyToExistingSubAccounts: false,
      applyToNewSubAccounts: false,
    }),
  });

const sampleConfig =
  new LoadListingsConfigurationResponse({
    alternateBrandNames: [
      new AltBrandNamesConfig({'id': 'testId', 'name': 'testName', 'description': 'testDescription'}),
      new AltBrandNamesConfig({'id': 'location.rank_tracking_alternate_names',
        'name': 'Rank Tracking Alternate Names',
        'description': 'Alternate names we can use to affect ranking of matches.'}),
    ],
    existingConfig: new ListingConfiguration({
      profileFieldId: 'location.rank_tracking_alternate_names',
      deliveryConfigurationMap: new Map([
        [715, new DeliveryConfiguration(
          {dontSyncListings: true, dontCreateNewListings: true, automaticallyOptIn: false})],
        [559, new DeliveryConfiguration(
          {dontSyncListings: false, dontCreateNewListings: false, automaticallyOptIn: false})],
        [0, new DeliveryConfiguration(
          {dontSyncListings: true, dontCreateNewListings: false, automaticallyOptIn: false})],
      ]),
      applyToExistingSubAccounts: false,
      applyToNewSubAccounts: true,
    }),
  });

export const getListingConfigurationMocks = {
  success: abortSignal => mockRequest(abortSignal, resolve => resolve(sampleConfig)),
  successDefault: abortSignal => mockRequest(abortSignal, resolve => resolve(defaultConfig)),
  failure: abortSignal => mockRequest(abortSignal, (_resolve, reject) => reject(false),
  ),
};
